import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { X, Tag, Users, UserPlus, Loader2, Clock, Camera, User } from 'lucide-react'
import { formatRelativeTime } from '@/lib/dateTime'
import { cn } from '@/lib/utils'
import { ClassificationChipGrid } from './ClassificationChipGrid'
import { PersonMatchList } from './PersonMatchList'
import { AddUnknownPersonForm } from './AddUnknownPersonForm'
import type { Classification, Person } from '@/types'

export interface DrawerFace {
  personId: string | null
  personName: string
  classificationId: string
  isUnmatched?: boolean
  thumbnailBase64?: string | null
}

export interface DrawerSightingContext {
  sightingId: string
  cameraName?: string
  timestamp: string
}

interface FeedActionDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  face: DrawerFace | null
  sighting: DrawerSightingContext | null
  classifications: Classification[]
  canManagePersons: boolean
  canManageClassifications: boolean
  onActionComplete: () => void
}

type Mode = 'classify' | 'match' | 'add'

export function FeedActionDrawer({
  open,
  onOpenChange,
  groupId,
  face,
  sighting,
  classifications,
  canManagePersons,
  canManageClassifications,
  onActionComplete,
}: FeedActionDrawerProps) {
  const { t } = useTranslation()
  const isUnknown = !!face?.isUnmatched || !face?.personId
  const [mode, setMode] = useState<Mode>('classify')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const availableModes: Mode[] = (() => {
    const modes: Mode[] = []
    if (canManageClassifications && !isUnknown) modes.push('classify')
    // Match merges from a source person, so it requires a matched face.
    // Unknown faces (no personId) can only be added as a new person.
    if (canManagePersons && !isUnknown) modes.push('match')
    if (canManagePersons && isUnknown) modes.push('add')
    return modes
  })()

  // Reset internal state when drawer opens or face changes
  useEffect(() => {
    if (open) {
      setError(null)
      setSuccess(null)
      setMode(availableModes[0] || 'classify')
    }
  }, [open, face?.personId, face?.isUnmatched])

  const handleClassify = async (classificationId: string) => {
    if (!face?.personId || busy) return
    setBusy(true)
    setError(null)
    try {
      const fn = httpsCallable(functions, 'updatePerson')
      await fn({ groupId, personId: face.personId, classificationId })
      const cls = classifications.find((c) => c.id === classificationId)
      setSuccess(t('feed.drawer.classifySuccess', { name: cls?.displayName || '' }))
      onActionComplete()
      setTimeout(() => onOpenChange(false), 800)
    } catch (err: any) {
      console.error('Failed to update classification:', err)
      setError(err?.message || t('feed.drawer.failedToClassify'))
    } finally {
      setBusy(false)
    }
  }

  const handleMatch = async (target: Person) => {
    if (!face?.personId || busy) return
    setBusy(true)
    setError(null)
    try {
      const fn = httpsCallable(functions, 'mergePersons')
      await fn({
        groupId,
        sourcePersonId: face.personId,
        targetPersonId: target.id,
      })
      setSuccess(t('feed.drawer.matchSuccess', { name: target.name || '' }))
      onActionComplete()
      setTimeout(() => onOpenChange(false), 800)
    } catch (err: any) {
      console.error('Failed to merge persons:', err)
      setError(err?.message || t('feed.drawer.failedToMatch'))
    } finally {
      setBusy(false)
    }
  }

  const handleCreated = () => {
    setSuccess(t('feed.drawer.createSuccess'))
    onActionComplete()
    setTimeout(() => onOpenChange(false), 800)
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-ink/55 backdrop-blur-[4px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[60] flex flex-col bg-[rgba(246,241,230,0.98)] shadow-auvers-modal',
            // Mobile: bottom sheet
            'inset-x-0 bottom-0 max-h-[88vh] rounded-t-2xl border-t border-white/70',
            'pb-[env(safe-area-inset-bottom)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            // Desktop ≥sm: right side panel, full height
            'sm:inset-y-0 sm:right-0 sm:bottom-auto sm:left-auto sm:h-full sm:w-[440px] sm:max-h-none',
            'sm:rounded-none sm:rounded-l-2xl sm:border-l sm:border-t-0',
            'sm:data-[state=closed]:slide-out-to-right sm:data-[state=open]:slide-in-from-right'
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-rule px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="font-serif text-[20px] font-medium leading-tight text-ink">
                {isUnknown ? t('feed.drawer.unknownFace') : face?.personName}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-1 text-[12px] text-ink-soft">
                {t('feed.drawer.subtitle')}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-ink/15 text-ink transition hover:bg-ink/5"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* Mode tabs */}
          {availableModes.length > 1 && (
            <div className="flex gap-1 border-b border-rule px-3 py-2">
              {availableModes.includes('classify') && (
                <ModeTab
                  active={mode === 'classify'}
                  onClick={() => setMode('classify')}
                  icon={<Tag className="h-4 w-4" />}
                  label={t('feed.drawer.tabClassify')}
                />
              )}
              {availableModes.includes('add') && (
                <ModeTab
                  active={mode === 'add'}
                  onClick={() => setMode('add')}
                  icon={<UserPlus className="h-4 w-4" />}
                  label={t('feed.drawer.tabAdd')}
                />
              )}
              {availableModes.includes('match') && (
                <ModeTab
                  active={mode === 'match'}
                  onClick={() => setMode('match')}
                  icon={<Users className="h-4 w-4" />}
                  label={t('feed.drawer.tabMatch')}
                />
              )}
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {/* Summary card: who, when, where */}
            {face && (
              <div className="mb-4 flex items-center gap-3 rounded-xl border border-rule bg-paper-2/50 p-3">
                <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-lg border border-rule bg-ink/5">
                  {face.thumbnailBase64 ? (
                    <img
                      src={`data:image/jpeg;base64,${face.thumbnailBase64}`}
                      alt={isUnknown ? t('feed.drawer.unknownFace') : face.personName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-7 w-7 text-ink-soft" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-[16px] font-medium leading-tight text-ink">
                    {isUnknown ? t('feed.drawer.unknownFace') : face.personName}
                  </p>
                  {sighting && (
                    <div className="mt-1.5 space-y-1 text-[12px] text-ink-soft">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        <span>{formatRelativeTime(sighting.timestamp)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Camera className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {sighting.cameraName || t('feed.drawer.unknownCamera')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert className="mb-4">
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}
            {busy && (
              <div className="mb-4 flex items-center gap-2 text-[13px] text-ink-soft">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('feed.drawer.working')}
              </div>
            )}

            {availableModes.length === 0 && (
              <p className="py-8 text-center text-[14px] text-ink-soft">
                {t('feed.drawer.noActionsAvailable')}
              </p>
            )}

            {mode === 'classify' && availableModes.includes('classify') && (
              <ClassificationChipGrid
                classifications={classifications}
                selectedId={face?.classificationId}
                onSelect={handleClassify}
                disabled={busy}
              />
            )}

            {mode === 'add' && availableModes.includes('add') && (
              <AddUnknownPersonForm
                groupId={groupId}
                classifications={classifications}
                faceThumbnailBase64={face?.thumbnailBase64 ?? null}
                onCreated={handleCreated}
              />
            )}

            {mode === 'match' && availableModes.includes('match') && (
              <PersonMatchList
                groupId={groupId}
                excludePersonId={face?.personId}
                onSelect={handleMatch}
                disabled={busy}
              />
            )}
          </div>

          <div className="border-t border-rule px-5 py-3 sm:hidden">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onOpenChange(false)}
            >
              {t('common.close')}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function ModeTab({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-[13px] font-medium transition',
        active
          ? 'bg-paper-3 text-ink shadow-sm'
          : 'text-ink-soft hover:bg-paper-2'
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  )
}
